import type { BaseUser } from "@models";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import type { DocStatus, RegulatoryTypeEnum } from "@types";
import type { ReactNode } from "react";
import { Chip as FilterChip } from "src/components/Chip/Chip";
import {
  type DocumentsActionFilterValue,
  documentsActionOptions,
  type FilterOption,
} from "./Documents.filtering";
import type { UseDocumentsFiltersResult } from "./useDocumentsFilters";

type DocumentsFilterMenuContentProps = Pick<
  UseDocumentsFiltersResult,
  "actions" | "filters" | "options" | "selectedUsers"
>;

type ChipSectionProps = {
  onToggle: (value: string) => void;
  options: FilterOption<string>[];
  selectedValues: string[];
  testIdPrefix: string;
  title: string;
};

const selectedChipSx = {
  "&&": {
    backgroundColor: "#5d85ff2b",
    color: "inherit",
  },
  "&&:hover": {
    backgroundColor: "#5d85ff2b",
    color: "inherit",
  },
};

const FilterSection = ({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) => (
  <Box sx={{ pb: 0.5 }}>
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        margin: "4px 14px 0",
        padding: "6px 8px 5px",
        width: "calc(100% - 28px)",
      }}
    >
      <Typography fontSize="h6">{title}</Typography>
    </Box>
    {children}
  </Box>
);

const ChipSection = ({
  onToggle,
  options,
  selectedValues,
  testIdPrefix,
  title,
}: ChipSectionProps) =>
  options.length > 0 ? (
    <FilterSection title={title}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mx: 1.5,
          my: 1,
          px: 1,
        }}
      >
        {options.map(({ label, value }) => {
          const isSelected = selectedValues.includes(value);

          return (
            <FilterChip
              aria-pressed={isSelected}
              color={isSelected ? "info" : "default"}
              data-testid={`${testIdPrefix}-${value}`}
              key={value}
              label={label}
              onClick={() => onToggle(value)}
              size="small"
              sx={{
                width: "fit-content",
                ...(isSelected ? selectedChipSx : {}),
              }}
            />
          );
        })}
      </Box>
    </FilterSection>
  ) : null;

const UserSection = ({
  label,
  onChange,
  title,
  users,
  value,
}: {
  label: string;
  onChange: (users: BaseUser[]) => void;
  title: string;
  users: BaseUser[];
  value: BaseUser[];
}) =>
  users.length > 0 ? (
    <FilterSection title={title}>
      <Box sx={{ margin: "4px 14px" }}>
        <Autocomplete
          multiple
          disablePortal
          filterSelectedOptions
          getOptionLabel={(option) =>
            `${option.firstName ?? ""} ${option.lastName ?? ""}`.trim() ||
            option.email
          }
          isOptionEqualToValue={(option, selectedValue) =>
            option.id === selectedValue.id
          }
          onChange={(_, newValue) => onChange(newValue)}
          options={users}
          renderInput={(params) => <TextField {...params} label={label} />}
          size="small"
          value={value}
        />
      </Box>
    </FilterSection>
  ) : null;

export const DocumentsFilterMenuContent = ({
  actions,
  filters,
  options,
  selectedUsers,
}: DocumentsFilterMenuContentProps) => {
  const scopeOptions = [
    { label: "Org-only docs", value: "org-only-docs" },
    ...options.deviceOptions,
  ];
  const chipSections: ChipSectionProps[] = [
    {
      title: "Scope",
      options: scopeOptions,
      selectedValues: filters.isOrgOnlySelected
        ? ["org-only-docs", ...filters.selectedDeviceIds]
        : filters.selectedDeviceIds,
      onToggle: (value) =>
        value === "org-only-docs"
          ? actions.toggleOrgOnly()
          : actions.toggleDevice(value),
      testIdPrefix: "documents-filter-scope-option",
    },
    {
      title: "Status",
      options: options.statusOptions,
      selectedValues: filters.selectedStatuses,
      onToggle: (value) => actions.toggleStatus(value as DocStatus),
      testIdPrefix: "documents-filter-status-option",
    },
    {
      title: "Functional group",
      options: options.functionalGroupOptions,
      selectedValues: filters.selectedFunctionalGroupIds,
      onToggle: actions.toggleFunctionalGroup,
      testIdPrefix: "documents-filter-functional-group-option",
    },
    {
      title: "Document type",
      options: options.documentTypeOptions,
      selectedValues: filters.selectedDocumentTypes,
      onToggle: (value) =>
        actions.toggleDocumentType(value as RegulatoryTypeEnum),
      testIdPrefix: "documents-filter-document-type-option",
    },
    {
      title: "Actions",
      options: documentsActionOptions,
      selectedValues: filters.selectedActionFilters,
      onToggle: (value) =>
        actions.toggleActionFilter(value as DocumentsActionFilterValue),
      testIdPrefix: "documents-filter-action-option",
    },
  ];

  return (
    <div
      data-testid="documents-filter-menu-content"
      className="min-h-0"
      style={{
        minHeight: 0,
        overflow: "visible",
        paddingTop: "0",
        width: "100%",
      }}
    >
      <div
        data-testid="documents-filter-menu-body"
        className="min-h-0"
        style={{ minHeight: 0, overflowY: "visible" }}
      >
        {chipSections.map((section) => (
          <ChipSection key={section.title} {...section} />
        ))}
        <UserSection
          title="Authors"
          label="Select Authors"
          users={options.authorUsers}
          value={selectedUsers.authors}
          onChange={actions.setSelectedAuthorUsers}
        />
        <UserSection
          title="Approvers"
          label="Select Approvers"
          users={options.approverUsers}
          value={selectedUsers.approvers}
          onChange={actions.setSelectedApproverUsers}
        />
      </div>
    </div>
  );
};
